import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionContainer,
  EndShiftButton
} from './styles';

import ActionModal from '../../../../../components/ActionModal';
import CancelShift from './components/CancelShift';
import {
  cancelShift,
  forceCancelShift,
  selectCancelShiftLoading,
  selectCancelShiftSuccess,
  selectCancelShiftError,
  selectCancelShiftMessage,
  selectForceCancelShiftLoading,
  selectForceCancelShiftSuccess,
  selectForceCancelShiftError,
  selectForceCancelShiftMessage,
} from '../../../../../modules/shift';
import ConfirmationDialog from '../../../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../../../components/common/AlertPopup'; // Import AlertPopup

interface ShiftActionProps {
  status: string;
  shiftId: number;
}

const ShiftAction: React.FC<ShiftActionProps> = ({
  status,
  shiftId
}) => {
  const dispatch = useDispatch<any>();
  let cacnelShift = (status === 'scheduled' || status === 'partially_assigned' || status === 'assigned');

  const loading = useSelector(selectCancelShiftLoading);
  const success = useSelector(selectCancelShiftSuccess);
  const error = useSelector(selectCancelShiftError);
  const message = useSelector(selectCancelShiftMessage);

  const forceLoading = useSelector(selectForceCancelShiftLoading);
  const forceSuccess = useSelector(selectForceCancelShiftSuccess);
  const forceError = useSelector(selectForceCancelShiftError);
  const forceMessage = useSelector(selectForceCancelShiftMessage);

  const [openActionModal, setOpenActionModal] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openForceConfirmation, setOpenForceConfirmation] = React.useState(false);
  const [cancelData, setCancelData] = React.useState<{ reason: string; notes: string } | null>(null);
  const [openAlert, setOpenAlert] = React.useState(false); // State for AlertPopup
  const [alertMessage, setAlertMessage] = React.useState(''); // Message for AlertPopup
  const [alertSeverity, setAlertSeverity] = React.useState<'success' | 'error'>('success'); // Severity for AlertPopup

  const onEndShift = (shiftId: number) => {
    setOpenActionModal(true);
  }

  const onCancelShift = (data: { reason: string; notes: string }) => {
    setCancelData(data);
    setOpenConfirmation(true);
  };

  const handleConfirmCancel = () => {
    if (cancelData) {
      dispatch(cancelShift({ shiftId, reason: cancelData.reason, additionalReason: cancelData.notes }));
    }
    setOpenConfirmation(false);
  };

  const handleForceCancel = () => {
    if (cancelData) {
      dispatch(forceCancelShift({ shiftId, cancellation_reason: cancelData.reason }));
    }
    setOpenForceConfirmation(false);
  };

  useEffect(() => {
    if (success) {
      setOpenActionModal(false);
      setAlertMessage(message || 'Shift cancelled successfully');
      setAlertSeverity('success');
      setOpenAlert(true); // Open AlertPopup
    }
  }, [success, message]);

  useEffect(() => {
    if (error) {
      if (error === "Shift cancellation time has passed, if you cancel now charge may applied") {
        setOpenForceConfirmation(true);
      } else {
        setAlertMessage(error || 'Failed to cancel shift');
        setAlertSeverity('error');
        setOpenAlert(true); // Open AlertPopup
      }
    }
  }, [error]);

  useEffect(() => {
    if (forceSuccess) {
      setOpenActionModal(false);
      setAlertMessage(forceMessage || 'Shift force cancelled successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
    }
  }, [forceSuccess, forceMessage]);

  useEffect(() => {
    if (forceError) {
      setAlertMessage(forceError || 'Failed to force cancel shift');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [forceError]);

  return (
    <ActionContainer>
      {cacnelShift &&
        <>
          <EndShiftButton
            onClick={() => { onEndShift(shiftId) }}
            disabled={loading || forceLoading}
          >
            {loading || forceLoading ? 'Cancelling Shift...' : 'Cancel Shift'}
          </EndShiftButton>
          <ActionModal
            open={openActionModal}
            onClose={() => setOpenActionModal(false)}
            height="70vh"
          >
            <CancelShift
              onCancelShift={onCancelShift}
              onKeepShift={() => setOpenActionModal(false)}
              shiftId={shiftId}
            />
          </ActionModal>
        </>
      }
      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleConfirmCancel}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel this shift?"
      />
      <ConfirmationDialog
        open={openForceConfirmation}
        onClose={() => setOpenForceConfirmation(false)}
        onConfirm={handleForceCancel}
        title="Force Cancellation Required"
        message="Shift cancellation time has passed. Charges may apply. Do you want to proceed with force cancellation?"
      />
      {/* AlertPopup for success/error messages */}
      <AlertPopup
        open={openAlert}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setOpenAlert(false)}
      />
    </ActionContainer>
  );
};

export default ShiftAction;